import React from 'react';
import { Box, Button, useColorModeValue, useDisclosure, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { SiteDataToSave } from 'clipsal-cortex-types/src/api';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { useReduxDispatch } from '../../app/store';
import { patch } from '../../common/api/api-helpers';
import { PostcodeFormData, PostcodeInput } from '../sites/PostcodeInput';
import { makePostcodeFormSchema } from './site-helpers';
import { selectSite, setCurrentSite } from './siteSlice';

type UpdateSiteAddressPayload = Omit<SiteDataToSave, 'site_name' | 'lat' | 'lon'>;

type SiteFormData = {
  address: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
};
type Props = {
  onZipcodeUpdated: (zipCode: string) => void;
  text: string;
};
// Provides a text button that opens a modal to update site address data via the zipcode input
export function UpdateZipcodeButton({ onZipcodeUpdated, text }: Props) {
  const toast = useToast({ isClosable: true });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const site = useSelector(selectSite);
  const dispatch = useReduxDispatch();

  const { t } = useTranslation();
  const linkButtonColor = useColorModeValue('schneiderSkyBlue.600', 'schneiderSkyBlue.200');
  const schema = yup.object().shape({
    ...makePostcodeFormSchema(t),
  });
  const {
    control,
    getValues,
    reset,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SiteFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      postCode: site.post_code ?? '',
    },
  });

  function handleSelectLocation(location: PostcodeFormData) {
    const currentValues = getValues();
    reset({
      ...currentValues,
      ...location,
    });
  }

  async function onSubmit(values: SiteFormData) {
    const body: UpdateSiteAddressPayload = {
      address: values.address,
      city: values.city ?? site.city,
      post_code: values.postCode ?? site.post_code,
      state: values.state ?? site.state,
      country: values.country ?? site.country,
      timezone: values.timezone ?? site.timezone,
    };

    try {
      const siteData = await patch<SiteDataToSave>(`/v1/sites/${site.site_id}`, body);
      dispatch(setCurrentSite({ ...site, ...siteData }));
      onClose();
      onZipcodeUpdated(siteData.post_code);
    } catch (e) {
      console.log(e);
      toast({ title: t('Set Address.error updating home'), status: 'error', duration: 3000 });
    }
  }
  return (
    <>
      <Button
        variant="link"
        px={0}
        minWidth={'fit-content'}
        w="fit-content"
        fontWeight={400}
        fontSize={15}
        color={linkButtonColor}
        onClick={onOpen}
        data-testid="change-btn"
      >
        {text}
      </Button>

      <AlertDialogModal
        isOpen={isOpen}
        onClose={onClose}
        header={t('Set Address.edit zip code')}
        confirmButtonName={t('Common.confirm')}
        cancelButtonName={t('Common.cancel')}
        isConfirming={isSubmitting}
        onConfirm={handleFormSubmit(onSubmit)}
        confirmButtonProps={{ type: 'submit' }}
        closeOnConfirm={false}
      >
        <Box m={2}>
          <PostcodeInput
            control={control}
            error={errors?.postCode?.message}
            handleSelectLocation={handleSelectLocation}
          />
        </Box>
      </AlertDialogModal>
    </>
  );
}
